import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Lightning, Play, Pause, Trash, PencilSimple,
  WhatsappLogo, EnvelopeSimple, ChatCircle, UserSwitch,
  ArrowsClockwise, Tag, Note, CaretRight, Clock, ListChecks,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TRIGGER_LABELS = {
  // lead_score_above retired 2026-08-04 (#8): lead score hidden from UI + automation disabled.
  stage_change: 'Stage Change',
  new_lead: 'New Lead Created',
  follow_up_overdue: 'Follow-up Overdue',
  tag_added: 'Tag Added',
  no_activity_days: 'No Activity (Days)',
};

const ACTION_LABELS = {
  send_whatsapp: 'Send WhatsApp',
  send_sms: 'Send SMS',
  send_email: 'Send Email',
  assign_counselor: 'Assign Counselor',
  change_stage: 'Change Stage',
  add_tag: 'Add Tag',
  add_note: 'Add Note',
};

const ACTION_ICONS = {
  send_whatsapp: WhatsappLogo,
  send_sms: ChatCircle,
  send_email: EnvelopeSimple,
  assign_counselor: UserSwitch,
  change_stage: ArrowsClockwise,
  add_tag: Tag,
  add_note: Note,
};

const ACTION_COLORS = {
  send_whatsapp: '#25D366',
  send_sms: '#2B6EF0',
  send_email: '#8B5CF6',
  assign_counselor: '#F97316',
  change_stage: '#E04B2A',
  add_tag: '#10A882',
  add_note: '#808080',
};

const TRIGGER_COLORS = {
  stage_change: '#E04B2A',
  new_lead: '#10A882',
  lead_score_above: '#F97316',
  follow_up_overdue: '#EF4444',
  tag_added: '#2B6EF0',
  no_activity_days: '#8B5CF6',
};

const EMPTY_FORM = {
  name: '',
  description: '',
  trigger_type: 'new_lead',
  trigger_config: {},
  actions: [],
  status: 'active',
};

function TriggerConfigFields({ triggerType, config, onChange }) {
  switch (triggerType) {
    case 'stage_change':
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">From Stage</Label>
            <select value={config.from_stage || ''} onChange={e => onChange({ ...config, from_stage: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
              <option value="">Any</option>
              {['new_inquiry', 'contacted', 'qualified', 'demo_scheduled', 'demo_attended', 'application', 'fee_pending'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">To Stage</Label>
            <select value={config.to_stage || ''} onChange={e => onChange({ ...config, to_stage: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
              <option value="">Any</option>
              {['contacted', 'qualified', 'demo_scheduled', 'demo_attended', 'application', 'fee_pending', 'enrolled', 'lost'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
      );
    case 'lead_score_above':
      return (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Score Threshold</Label>
          <Input type="number" min={1} max={100} value={config.threshold || ''} onChange={e => onChange({ ...config, threshold: parseInt(e.target.value) || 0 })} className="h-9 rounded-lg text-sm" placeholder="e.g. 80" />
        </div>
      );
    case 'follow_up_overdue':
    case 'no_activity_days':
      return (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Days</Label>
          <Input type="number" min={1} value={config.days || ''} onChange={e => onChange({ ...config, days: parseInt(e.target.value) || 0 })} className="h-9 rounded-lg text-sm" placeholder="e.g. 3" />
        </div>
      );
    case 'new_lead':
      return (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Source Filter (optional)</Label>
          <Input value={config.source || ''} onChange={e => onChange({ ...config, source: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="e.g. Meta Ads" />
        </div>
      );
    case 'tag_added':
      return (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Tag</Label>
          <Input value={config.tag || ''} onChange={e => onChange({ ...config, tag: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="e.g. scholarship" />
        </div>
      );
    default:
      return null;
  }
}

function ActionRow({ action, index, onRemove, onUpdate }) {
  const Icon = ACTION_ICONS[action.type] || Lightning;
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/30">
      <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACTION_COLORS[action.type] || '#808080'}15` }}>
        <Icon size={14} weight="fill" style={{ color: ACTION_COLORS[action.type] || '#808080' }} />
      </div>
      <div className="flex-1 min-w-0">
        <select
          value={action.type}
          onChange={e => onUpdate(index, { ...action, type: e.target.value })}
          className="h-7 w-full rounded-md text-[11px] border border-border px-2 bg-card text-foreground font-semibold"
          data-testid={`action-type-${index}`}
        >
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <button onClick={() => onRemove(index)} className="p-1 text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-action-${index}`}>
        <Trash size={13} />
      </button>
    </div>
  );
}

export default function AutomationPage() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [logsOpen, setLogsOpen] = useState(null);
  const [logs, setLogs] = useState([]);
  const [toggling, setToggling] = useState(null);

  const fetchAutomations = useCallback(async () => {
    try {
      const res = await api.get('/api/automations');
      setAutomations(res.data.automations || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAutomations(); }, [fetchAutomations]);

  const handleToggle = async (id) => {
    setToggling(id);
    try {
      const res = await api.put(`/api/automations/${id}/toggle`);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, status: res.data.status } : a));
      toast.success(`Automation ${res.data.status}`);
    } catch { toast.error('Toggle failed'); }
    finally { setToggling(null); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/automations/${id}`);
      setAutomations(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const openEdit = (auto) => {
    setEditId(auto.id);
    setForm({
      name: auto.name,
      description: auto.description || '',
      trigger_type: auto.trigger_type,
      trigger_config: auto.trigger_config || {},
      actions: auto.actions || [],
      status: auto.status,
    });
    setShowCreate(true);
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setShowCreate(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.actions.length === 0) { toast.error('Add at least one action'); return; }
    try {
      if (editId) {
        await api.put(`/api/automations/${editId}`, form);
        toast.success('Updated');
      } else {
        await api.post('/api/automations', form);
        toast.success('Created');
      }
      setShowCreate(false);
      setEditId(null);
      setForm({ ...EMPTY_FORM });
      fetchAutomations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const addAction = () => {
    setForm(prev => ({ ...prev, actions: [...prev.actions, { type: 'send_whatsapp', config: {} }] }));
  };
  const removeAction = (idx) => {
    setForm(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== idx) }));
  };
  const updateAction = (idx, action) => {
    setForm(prev => ({ ...prev, actions: prev.actions.map((a, i) => i === idx ? action : a) }));
  };

  const viewLogs = async (id) => {
    setLogsOpen(id);
    try {
      const res = await api.get(`/api/automations/${id}/logs`);
      setLogs(res.data.logs || []);
    } catch { setLogs([]); }
  };

  const activeCount = automations.filter(a => a.status === 'active').length;
  const totalExec = automations.reduce((sum, a) => sum + (a.execution_count || 0), 0);

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  return (
    <div data-testid="automation-page" className="stagger-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Automations</h1>
          <p className="text-sm text-muted-foreground mt-1">Build trigger-action workflows to automate your CRM</p>
        </div>
        <Button data-testid="create-automation-btn" onClick={openCreate} className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
          <Plus size={14} weight="bold" /> New Automation
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total', value: automations.length, sub: 'automations' },
          { label: 'Active', value: activeCount, sub: 'running now', color: '#10A882' },
          { label: 'Executions', value: totalExec, sub: 'total runs', color: '#E04B2A' },
        ].map(s => (
          <Card key={s.label} className="border border-border rounded-xl">
            <CardContent className="p-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">{s.label}</p>
              <p className="font-mono text-2xl font-bold" style={{ color: s.color || 'hsl(var(--foreground))' }} data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Automation Cards */}
      {automations.length === 0 ? (
        <Card className="border border-border rounded-xl">
          <CardContent className="p-12 text-center">
            <Lightning size={36} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No automations yet</p>
            <Button onClick={openCreate} className="mt-4 h-8 text-xs rounded-lg font-semibold text-white" style={{ backgroundColor: '#E04B2A' }}>Create your first</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {automations.map((auto) => {
            const trigColor = TRIGGER_COLORS[auto.trigger_type] || '#808080';
            return (
              <Card key={auto.id} data-testid={`automation-card-${auto.id}`} className="border border-border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-sm">
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Left accent bar */}
                    <div className="w-1 shrink-0" style={{ backgroundColor: auto.status === 'active' ? trigColor : 'hsl(var(--border))' }} />

                    <div className="flex-1 p-4">
                      {/* Top row: name + controls */}
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-display text-[15px] font-bold text-foreground truncate">{auto.name}</h3>
                            <span
                              className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{
                                color: auto.status === 'active' ? '#10A882' : 'hsl(var(--muted-foreground))',
                                backgroundColor: auto.status === 'active' ? 'rgba(16,168,130,0.1)' : 'hsl(var(--secondary))',
                              }}
                              data-testid={`automation-status-${auto.id}`}
                            >
                              {auto.status}
                            </span>
                          </div>
                          {auto.description && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{auto.description}</p>}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Switch
                            checked={auto.status === 'active'}
                            onCheckedChange={() => handleToggle(auto.id)}
                            disabled={toggling === auto.id}
                            data-testid={`toggle-${auto.id}`}
                          />
                          <button onClick={() => openEdit(auto)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" data-testid={`edit-${auto.id}`}>
                            <PencilSimple size={14} />
                          </button>
                          <button onClick={() => handleDelete(auto.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-${auto.id}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Trigger → Actions flow */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Trigger chip */}
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-lg" style={{ backgroundColor: `${trigColor}12`, color: trigColor }}>
                          <Lightning size={11} weight="fill" />
                          {TRIGGER_LABELS[auto.trigger_type] || auto.trigger_type}
                          {auto.trigger_config && Object.keys(auto.trigger_config).length > 0 && (
                            <span className="font-mono opacity-70 ml-0.5">
                              ({Object.values(auto.trigger_config).join(', ')})
                            </span>
                          )}
                        </span>

                        <CaretRight size={10} className="text-muted-foreground/40" />

                        {/* Action chips */}
                        {(auto.actions || []).map((action, i) => {
                          const AIcon = ACTION_ICONS[action.type] || Lightning;
                          const aColor = ACTION_COLORS[action.type] || '#808080';
                          return (
                            <span key={i} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg" style={{ backgroundColor: `${aColor}10`, color: aColor }}>
                              <AIcon size={11} weight="fill" />
                              {ACTION_LABELS[action.type] || action.type}
                            </span>
                          );
                        })}
                      </div>

                      {/* Bottom meta */}
                      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1 font-mono">
                          <Play size={10} />
                          {auto.execution_count || 0} runs
                        </span>
                        {auto.last_executed && (
                          <span className="flex items-center gap-1 font-mono">
                            <Clock size={10} />
                            Last: {new Date(auto.last_executed).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <button
                          onClick={() => viewLogs(auto.id)}
                          className="flex items-center gap-1 font-mono hover:text-foreground transition-colors"
                          data-testid={`view-logs-${auto.id}`}
                        >
                          <ListChecks size={10} />
                          Logs
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditId(null); } else setShowCreate(true); }}>
        <DialogContent className="rounded-xl max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-foreground text-lg">{editId ? 'Edit' : 'New'} Automation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label>
              <Input data-testid="automation-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="e.g. Auto-assign new leads" required />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Description</Label>
              <Textarea data-testid="automation-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-lg text-sm min-h-[60px]" placeholder="What does this automation do?" />
            </div>

            {/* Trigger */}
            <div className="border border-border rounded-xl p-3 space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#E04B2A' }}>
                <Lightning size={12} weight="fill" /> Trigger
              </p>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">When...</Label>
                <select
                  data-testid="trigger-type"
                  value={form.trigger_type}
                  onChange={e => setForm({ ...form, trigger_type: e.target.value, trigger_config: {} })}
                  className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"
                >
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <TriggerConfigFields triggerType={form.trigger_type} config={form.trigger_config} onChange={c => setForm({ ...form, trigger_config: c })} />
            </div>

            {/* Actions */}
            <div className="border border-border rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#2B6EF0' }}>
                  <CaretRight size={12} weight="bold" /> Actions ({form.actions.length})
                </p>
                <Button type="button" onClick={addAction} variant="outline" className="h-7 text-[10px] rounded-md gap-1 px-2 font-semibold" data-testid="add-action-btn">
                  <Plus size={10} /> Add
                </Button>
              </div>
              {form.actions.length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-2">No actions yet — click Add</p>
              )}
              <div className="space-y-2">
                {form.actions.map((action, i) => (
                  <ActionRow key={i} action={action} index={i} onRemove={removeAction} onUpdate={updateAction} />
                ))}
              </div>
            </div>

            <Button type="submit" data-testid="submit-automation-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
              {editId ? 'Update' : 'Create'} Automation
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Execution Logs Dialog */}
      <Dialog open={!!logsOpen} onOpenChange={(open) => { if (!open) setLogsOpen(null); }}>
        <DialogContent className="rounded-xl max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-foreground text-lg">Execution Logs</DialogTitle>
          </DialogHeader>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No execution logs yet</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-mono font-bold uppercase ${log.status === 'success' ? 'text-emerald-500' : 'text-destructive'}`}>{log.status}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{log.executed_at ? new Date(log.executed_at).toLocaleString('en-IN') : '-'}</span>
                  </div>
                  {log.details && <p className="text-[11px] text-muted-foreground">{log.details}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
